import { zodResolver } from '@hookform/resolvers/zod';
import { TrashIcon } from '@phosphor-icons/react';
import { ReactNode, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/modules/common/components/multi-select';

import { useEventParticipantTypes } from '../api';
import { EventStationResponseItem, StationsFormValues } from '../types';
import { stationsSchema } from '../validations';

const defaultStations: Omit<EventStationResponseItem, 'id'> = {
  name: '',
  accessTypes: [],
};

interface StationFormProps {
  eventId: string;
  stations: EventStationResponseItem[];
  onSubmit: (data: StationsFormValues) => Promise<void>;
  children: ReactNode;
}

export function StationForm({
  eventId,
  onSubmit,
  stations,
  children,
}: StationFormProps) {
  const { participantTypes, isFetching: isFetchingParticipantTypes } =
    useEventParticipantTypes(eventId, true);

  const participantTypesOptions = useMemo(
    () =>
      participantTypes.map((type) => ({
        value: type.id,
        id: type.value,
      })),
    [participantTypes]
  );

  const defaultValues = useMemo(() => {
    return {
      stations:
        stations.length > 0
          ? stations.map((s) => ({
              ...s,
              accessTypeIds: s.accessTypes.map((a) => a.id),
            }))
          : [defaultStations],
    };
  }, [stations]);

  const form = useForm<StationsFormValues>({
    resolver: zodResolver(stationsSchema),
    defaultValues,
  });

  const { append, remove, fields } = useFieldArray({
    control: form.control,
    name: 'stations',
  });

  const addNewStation = () => {
    append(defaultStations);
  };

  const removeStation = (index: number) => {
    remove(index);
  };

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit(onSubmit)(event);
          }}
          className="py-6"
        >
          <ScrollArea className="h-[400px]">
            <div className="flex flex-col max-w-6xl">
              {fields.map((item, index) => {
                return (
                  <div className="mx-1" key={`types.${item.id}.value`}>
                    <div className="flex justify-between">
                      <h4 className="text-zinc-50 font-semibold mb-5">
                        Station {index + 1}
                      </h4>
                      {fields.length > 1 && (
                        <Button
                          variant="outline"
                          onClick={() => removeStation(index)}
                        >
                          <TrashIcon />
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col gap-4">
                      <FormField
                        control={form.control}
                        name={`stations.${index}.name`}
                        data-testid={`stations.${item.id}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter a name"
                                maxLength={50}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <MultiSelect
                        name={`stations.${index}.accessTypeIds`}
                        label="Participant type"
                        control={form.control}
                        placeholder="Select one or more"
                        options={participantTypesOptions}
                        loading={isFetchingParticipantTypes}
                      />
                    </div>
                    {index !== fields.length - 1 && (
                      <Separator className="my-5" />
                    )}
                  </div>
                );
              })}

              <div className="mx-1">
                <Separator className="my-5" />
                <div>
                  <h4 className="text-zinc-50 font-semibold mb-5">
                    Station {fields.length + 1}
                  </h4>
                  <Button
                    variant="outline"
                    className="w-[130px]"
                    onClick={addNewStation}
                    data-testid="add-type-button"
                  >
                    + Add Station
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>
          {children}
        </form>
      </Form>
    </>
  );
}
